import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get } from 'es-toolkit/compat'
import { toLonLat, transformExtent } from 'ol/proj'
import type Map from 'ol/Map'
import type { ThreeDStatus } from 'state/map'

import * as c from 'common/c'
import useMap from 'hooks/use-map'
import { setThreeDEnabled, setThreeDStatus } from 'actions/map'
import { loadOlCesium } from 'common/ol/cesium-loader'
import { applyOlcsHints } from './olcs-layer-hints'
import { reloadGisImagery } from './gis-imagery'
import { gisCrossOrigin, onGisSessionRenewed } from 'common/geoserver'
import { clampExaggeration, relativeHeightFor } from './exaggeration'
import { applyPlatformCameraBindings } from './platform'
import { showReliefBaseLayer, applyBareTerrainColor } from './surface'
import {
  applyElevationRamp,
  rampStopsFromLegend,
  rampStopsFromRange,
} from './elevation-ramp'

/**
 * Owns the OLCesium instance behind the 3D map.
 *
 * All the imperative work lives here so the components around it stay
 * declarative, and every decision worth testing has been pushed out into the
 * pure modules beside this file.
 */

export type { ThreeDStatus }

const DEM_LAYER_NAME = 'dem'
// A stable fallback: a fresh [] per call makes useSelector see a new result
// every time and re-render, and defeats the useMemo keyed on it.
const NO_LAYER_INFO: never[] = []

/** Marks terrain as not yet applied, since `undefined` means 'no DEM'. */
const NEVER_APPLIED = Symbol('terrain-never-applied')

/**
 * Put the Cesium camera where the 2D view was looking.
 *
 * Framed from the view's own extent rather than by converting its resolution
 * into a camera distance. Cesium's setView already knows how to fit a
 * geographic rectangle to the viewport, so this needs no field of view, no
 * canvas height and no Web Mercator latitude correction — the three inputs that
 * ol-cesium's own conversion depends on and that are wrong or unmeasured while
 * the scene is still starting up.
 *
 * It also sidesteps an asymmetry in ol-cesium: readFromView() positions the
 * camera relative to the ellipsoid (an OL centre carries no height) while
 * updateView() measures back to the terrain under the cursor, so the round trip
 * loses the ground elevation in the zoom-in direction.
 */
const frameViewExtent = (scene: any, map: Map, extent?: number[]): void => {
  const Cesium = window.Cesium as any
  if (!scene || !Cesium || !extent || extent.some((v) => !Number.isFinite(v))) {
    return
  }

  const [west, south, east, north] = transformExtent(
    extent,
    map.getView().getProjection(),
    'EPSG:4326',
  )
  if (east <= west || north <= south) return

  scene.camera.setView({
    destination: Cesium.Rectangle.fromDegrees(west, south, east, north),
  })
}

/** Resolves once the OL map has a target and a non-zero size. */
const whenMapSized = (map: Map): Promise<void> =>
  new Promise((resolve) => {
    const size = map.getSize()
    if (map.getTarget() && size && size[0] > 0 && size[1] > 0) {
      resolve()
      return
    }
    map.once('postrender', () => resolve())
  })

export const useOlCesium = (mapRef = 'map') => {
  const map = useMap(mapRef) as Map | undefined
  const dispatch = useDispatch()

  const enabled = useSelector((state) =>
    get(state, 'map.threeDEnabled', false),
  ) as boolean
  const surface = useSelector((state) =>
    get(state, 'map.threeDSurface', c.THREE_D_SURFACE.IMAGERY),
  ) as string
  const exaggeration = useSelector((state) =>
    get(state, 'map.verticalExaggeration', c.DEFAULT_VERTICAL_EXAGGERATION),
  ) as number

  // The harvest's DEM metadata, sourced from field.layerInfo rather than
  // map.wmsLayers: the elevation surface is base-map terrain, not a rendered
  // raster, so it must be available whether or not the user has switched the
  // DEM WMS layer on for 2D display. three-d-control gates the Elevation
  // option on this same layerInfo entry for the same reason.
  const layerInfo = useSelector((state) =>
    get(state, 'field.layerInfo', NO_LAYER_INFO),
  ) as {
    layerName?: string
    harvestId?: number
    bboxMinx?: number
    bboxMiny?: number
    bboxMaxx?: number
    bboxMaxy?: number
    customLegends?: { legendData?: unknown }[]
  }[]
  const demInfo = useMemo(
    () => layerInfo.find((layer) => layer.layerName === DEM_LAYER_NAME),
    [layerInfo],
  )
  // The harvest's DEM legend, so the globe is coloured with the same breaks the
  // 2D elevation layer and the legend panel already use.
  const demLegend = demInfo?.customLegends?.[0]?.legendData
  const demLayer = useMemo(() => {
    if (!demInfo) return undefined
    const { bboxMiny, bboxMinx, bboxMaxy, bboxMaxx } = demInfo
    // parseLayerBbox expects [south, west, north, east]; a missing coordinate
    // leaves bbox undefined, which createHarvestTerrain treats as no DEM.
    const bbox =
      bboxMiny === undefined ||
      bboxMinx === undefined ||
      bboxMaxy === undefined ||
      bboxMaxx === undefined
        ? undefined
        : [bboxMiny, bboxMinx, bboxMaxy, bboxMaxx]
    return { harvestId: demInfo.harvestId, bbox }
  }, [demInfo])
  // layerInfo is rebuilt on every legend round-trip, so the terrain effect
  // keys off the identity of the DEM rather than the array, or it would
  // thrash.
  const demLayerKey = demLayer
    ? `${demLayer.harvestId}:${demLayer.bbox?.join(',')}`
    : undefined

  const instanceRef = useRef<any>(null)
  const buildingRef = useRef(false)
  // Read from the build effect's async continuation, where `enabled` itself
  // would be the stale value captured when that build started.
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  // Bumped to re-run the build effect after a build was cancelled mid-flight
  // (an off/on toggle while Cesium or the terrain was still loading) and left
  // enabled with no instance and nothing else to change [enabled, map] and
  // wake the effect back up.
  const [rebuildToken, setRebuildToken] = useState(0)
  const detachRef = useRef<(() => void) | null>(null)
  const resizeDetachRef = useRef<(() => void) | null>(null)
  const hintsDetachRef = useRef<(() => void) | null>(null)
  const gisRenewalDetachRef = useRef<(() => void) | null>(null)
  // State rather than a ref: applyTerrain resolves asynchronously, and the
  // elevation-ramp effect below needs a render triggered once the new
  // harvest's field actually lands, or it keeps colouring the globe with the
  // previous harvest's p05/p95 indefinitely.
  const [demField, setDemField] = useState<{
    stats?: { p05: number; p95: number }
  } | null>(null)
  // `undefined` is a real key (the no-DEM case), so 'never run' needs its own.
  const appliedTerrainKeyRef = useRef<string | undefined | symbol>(
    NEVER_APPLIED,
  )
  // Kept locally as well as in the store: the effects below need to read it
  // synchronously in the same render they were scheduled from.
  const [status, setStatusLocal] = useState<ThreeDStatus>(c.THREE_D_STATUS.IDLE)

  const setStatus = (next: ThreeDStatus) => {
    setStatusLocal(next)
    dispatch(setThreeDStatus(next))
  }

  /**
   * Disable the scene and undo the rotation it leaves behind.
   *
   * setEnabled(false) writes the Cesium camera's heading back into the OL
   * view via camera_.updateView(), so leaving 3D at a rotated heading would
   * otherwise carry that rotation into the 2D map. Shared by the deliberate
   * toggle-off below and by teardown(), so a render failure or an unmount —
   * not just the user turning 3D off — also lands back on an unrotated map.
   */
  const disableAndResetRotation = (instance: any) => {
    instance.setEnabled(false)
    map?.getView().setRotation(0)
  }

  /**
   * Dispose of the scene and leave the 2D map exactly as it was.
   *
   * setEnabled(false) MUST come first: it restores the map's patched
   * addInteraction/removeInteraction and un-hides the OL layer group, so
   * destroying without it leaves the 2D map blank and inert. Clearing the ref
   * is what lets the build effect construct a fresh instance afterwards.
   */
  const teardown = () => {
    detachRef.current?.()
    detachRef.current = null
    resizeDetachRef.current?.()
    resizeDetachRef.current = null
    hintsDetachRef.current?.()
    hintsDetachRef.current = null
    gisRenewalDetachRef.current?.()
    gisRenewalDetachRef.current = null
    appliedTerrainKeyRef.current = NEVER_APPLIED

    const instance = instanceRef.current
    if (!instance) return
    instanceRef.current = null
    try {
      disableAndResetRotation(instance)
      instance.destroy()
    } catch (error) {
      // A lost context can make Cesium throw on the way down; the instance is
      // going away regardless and the 2D map has already been handed back.
      console.warn('Ignoring error while disposing the 3D scene', error)
    }
  }

  /**
   * Resolve and apply the terrain for the current harvest.
   *
   * Shared by the initial build and by later harvest changes, so the globe is
   * never shown against terrain it is about to replace.
   */
  const applyTerrain = async (scene: any, isCancelled: () => boolean) => {
    // Imported here rather than at module scope so the DEM reader — which
    // pulls in geotiff — stays out of the entry bundle for the majority of
    // sessions that never open 3D.
    const { createHarvestTerrain } = await import(
      /* webpackChunkName: "harvest-dem" */ 'common/cesium'
    )
    const mapCenter = map?.getView().getCenter()
    const terrain = await createHarvestTerrain({
      Cesium: window.Cesium as never,
      harvestId: demLayer?.harvestId,
      bbox: demLayer?.bbox,
      viewCenter: mapCenter
        ? (toLonLat(mapCenter) as [number, number])
        : undefined,
    })
    if (isCancelled()) return

    setDemField(terrain.field)
    scene.terrainProvider = terrain.provider as typeof scene.terrainProvider
    // Pivot exaggeration at the field's own elevation. Cesium's default is 0,
    // the ellipsoid, which at 3x would lift a 300 m field to 900 m instead of
    // revealing any of its relief.
    scene.verticalExaggerationRelativeHeight = relativeHeightFor(
      terrain.relativeHeight,
    )
    scene.requestRender?.()
    appliedTerrainKeyRef.current = demLayerKey
  }

  // Build once, on first enable. Rebuilding per toggle would leak a WebGL
  // context each time and re-download the terrain, so the instance is kept and
  // only its enabled flag is flipped afterwards.
  useEffect(() => {
    if (!enabled || !map || instanceRef.current || buildingRef.current) return

    buildingRef.current = true
    let cancelled = false
    setStatus(c.THREE_D_STATUS.LOADING)
    ;(async () => {
      try {
        // setEnabled throws unless the map has a target and a size, and
        // FarmtrxMap is what calls setTarget.
        await whenMapSized(map)
        const { default: OLCesium } = await loadOlCesium()
        if (cancelled) return

        // Cesium requests GIS proxy imagery itself. Off the proxy's origin
        // (partner hosts, local development) it sends the session cookie only
        // to servers it trusts.
        const gisOrigin = gisCrossOrigin()
        const trustedServers = window.Cesium?.TrustedServers as
          | {
              add: (host: string, port: number) => void
            }
          | undefined
        if (gisOrigin && trustedServers) {
          trustedServers.add(gisOrigin.host, gisOrigin.port)
        }

        // Before the constructor, not after: OLCesium synchronizes its layers
        // immediately on construction, and that is the moment each imagery
        // layer's rectangle is read from olcs_extent. Applied any later, the
        // harvest rasters get a whole-globe rectangle and Cesium asks GeoServer
        // for tiles across the entire planet at every level — enough concurrent
        // requests to exhaust the browser.
        //
        // Registering first also means our collection listener runs ahead of
        // ol-cesium's, so layers mounted later are hinted before their
        // counterparts are built.
        hintsDetachRef.current = applyOlcsHints(map.getLayers())

        const instance = new OLCesium({
          map,
          // Neutralises OL's pointer handling in 3D, which is what stops the 2D
          // click and draw machinery firing against a canvas that is no longer
          // showing what it thinks it is.
          stopOpenLayersEventsPropagation: true,
        })
        // Register immediately so every subsequent failure can use the common
        // teardown path to dispose Cesium and restore the OL map.
        instanceRef.current = instance
        gisRenewalDetachRef.current = onGisSessionRenewed(() =>
          reloadGisImagery(map.getLayers()),
        )

        const scene = instance.getCesiumScene()

        // ol-cesium sizes its container and canvas with width/height:100%, so
        // what they measure depends on layout having settled. Both directions
        // of its camera/view sync divide by scene.canvas.clientHeight
        // (calcDistanceForResolution and calcResolutionForDistance in
        // olcs/core), so a canvas that measures zero does not merely render
        // small — it collapses the camera distance to zero on the way in and
        // produces a garbage resolution on the way back out, which the frame
        // loop then writes into the OL view. That shows up as the view slamming
        // to maximum zoom and drifting off the field.
        //
        // Size it explicitly from the OL map instead. That figure is reliable:
        // it is the same one the 2D map renders correctly from.
        const applyMapSize = () => {
          const size = map.getSize()
          if (!size || !size[0] || !size[1]) return
          const [width, height] = size
          const canvas = scene.canvas as HTMLCanvasElement
          const container = canvas.parentElement
          if (container) {
            container.style.width = `${width}px`
            container.style.height = `${height}px`
          }
          canvas.style.width = `${width}px`
          canvas.style.height = `${height}px`
          scene.camera.frustum.aspectRatio = width / height
        }
        applyMapSize()
        map.on('change:size', applyMapSize)
        resizeDetachRef.current = () => map.un('change:size', applyMapSize)

        // Mac trackpads have no middle button and treat Ctrl+click as the
        // secondary click, so tilt gets an Option/Alt binding there.
        applyPlatformCameraBindings(scene, window.Cesium)

        scene.globe.depthTestAgainstTerrain = true
        scene.screenSpaceCameraController.minimumZoomDistance =
          c.THREE_D_MIN_ZOOM_DISTANCE_M
        // Sync the camera back into the OL view when a movement finishes
        // rather than on every rendered frame. The per-frame default means any
        // transient bad camera is immediately written into the view — and
        // because the view is what the camera is derived from, a single bad
        // frame during a pan is self-reinforcing rather than self-correcting.
        instance.setRefresh2DAfterCameraMoveEndOnly(true)

        // Idle detection. Without it Cesium renders continuously, which is felt
        // on the tablets this app already supports.
        instance.enableAutoRenderLoop()

        if (cancelled) {
          teardown()
          return
        }

        // Neither Cesium nor ol-cesium registers a webglcontextlost handler,
        // and Cesium defaults rethrowRenderErrors to false — so a GPU reset,
        // a driver crash or the browser reclaiming the context would otherwise
        // leave a frozen globe with no message and no way back. Far more likely
        // in practice than a machine with no GPU at all.
        const fail = (reason: string, detail?: unknown) => {
          console.error(`3D map stopped: ${reason}`, detail)
          teardown()
          setStatus(c.THREE_D_STATUS.FAILED)
          // The scene is unrecoverable, so drop to 2D rather than leaving the
          // user staring at a dead canvas. The next toggle builds a fresh one.
          dispatch(setThreeDEnabled(false))
        }

        const onRenderError = (_scene: unknown, error: unknown) =>
          fail('render error', error)
        const onContextLost = (event: Event) =>
          fail('WebGL context lost', event)

        scene.renderError.addEventListener(onRenderError)
        scene.canvas.addEventListener('webglcontextlost', onContextLost)

        detachRef.current = () => {
          scene.renderError.removeEventListener(onRenderError)
          scene.canvas.removeEventListener('webglcontextlost', onContextLost)
        }

        // Settle the terrain before the globe is ever shown. Swapping the
        // provider re-tessellates every tile and re-drapes the imagery over it,
        // so doing that after the scene is visible reads as the map loading a
        // second time.
        await applyTerrain(scene, () => cancelled)
        if (cancelled) {
          teardown()
          return
        }

        setStatus(c.THREE_D_STATUS.READY)
      } catch (error) {
        console.error('Failed to start 3D map', error)
        if (!cancelled) {
          teardown()
          setStatus(c.THREE_D_STATUS.FAILED)
          dispatch(setThreeDEnabled(false))
        }
      } finally {
        buildingRef.current = false
        // This build never got the chance to install an instance, but
        // enabled has since flipped back on with nothing left to change
        // [enabled, map] again — bump the token so the effect gets one more
        // run and actually starts the replacement build.
        if (cancelled && !instanceRef.current && enabledRef.current) {
          setRebuildToken((token) => token + 1)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, map, rebuildToken])

  // Destroy only on unmount; toggling merely flips the enabled flag.
  useEffect(() => () => teardown(), [])

  useEffect(() => {
    if (status !== c.THREE_D_STATUS.READY) return
    const instance = instanceRef.current
    if (!instance || !map) return

    if (!enabled) {
      disableAndResetRotation(instance)
      return
    }

    // The ground the 2D map was showing, which is what 3D has to reproduce.
    const entryExtent = map.getView().calculateExtent(map.getSize())

    // setEnabled() places the camera itself, from a resolution-to-distance
    // conversion that does not survive the ellipsoid/terrain mismatch, and then
    // *schedules* a frame. Re-framing on that frame meant the first one
    // rendered from the wrong camera and the entire view reloaded once it
    // moved — the flash. Correcting it in the same tick means no frame is ever
    // rendered from the wrong place. The canvas was sized at construction, so
    // the frustum is already measurable here.
    instance.setEnabled(true)
    frameViewExtent(instance.getCesiumScene(), map, entryExtent)

    // The OL view is deliberately left untouched: writing to it here would
    // trip ol-cesium's own view listener and move the camera a second time.
  }, [status, enabled, map])

  // Surface selection. Hiding the base map borrows the 2D map's own visibility
  // flags, so the restore in this cleanup is what keeps the 2D map intact.
  useEffect(() => {
    if (status !== c.THREE_D_STATUS.READY || !enabled || !map) return
    if (surface !== c.THREE_D_SURFACE.ELEVATION) return

    const scene = instanceRef.current?.getCesiumScene()
    const restoreLayers = showReliefBaseLayer(map.getLayers())
    // With the basemap gone the globe falls back to its own base colour, which
    // Cesium defaults to a deep ocean blue. This is what shows wherever the
    // ramp cannot be built.
    const restoreColor = applyBareTerrainColor(scene)

    // Prefer the harvest's own legend so 3D, the 2D layer and the legend panel
    // agree; otherwise span the DEM's own middle range, which ignores the GNSS
    // spikes that min/max would stretch the whole ramp around.
    const stats = demField?.stats
    const stops =
      rampStopsFromLegend(demLegend) ??
      (stats ? rampStopsFromRange(stats.p05, stats.p95) : null)
    const restoreRamp = applyElevationRamp(scene, stops)

    return () => {
      restoreRamp()
      restoreColor()
      restoreLayers()
    }
  }, [status, enabled, map, surface, demLegend, demLayerKey, demField])

  useEffect(() => {
    if (status !== c.THREE_D_STATUS.READY) return
    const scene = instanceRef.current?.getCesiumScene()
    if (!scene) return
    scene.verticalExaggeration = clampExaggeration(exaggeration)
    scene.requestRender?.()
  }, [status, exaggeration])

  // Terrain: the harvest's own DEM blended into the global terrain when one is
  // available, the global terrain alone otherwise. The initial terrain is
  // applied during the build, so this only handles later harvest changes.
  useEffect(() => {
    if (status !== c.THREE_D_STATUS.READY || !enabled) return

    const scene = instanceRef.current?.getCesiumScene()
    if (!scene) return

    // The build already applied terrain for the harvest selected when 3D
    // started, so only a genuine change needs acting on; re-applying the same
    // provider would re-tessellate every tile for nothing.
    if (appliedTerrainKeyRef.current === demLayerKey) return

    let cancelled = false
    void applyTerrain(scene, () => cancelled).catch((error) => {
      if (cancelled) return
      console.error('Failed to update 3D terrain', error)
      teardown()
      setStatus(c.THREE_D_STATUS.FAILED)
      dispatch(setThreeDEnabled(false))
    })

    return () => {
      cancelled = true
    }
  }, [status, enabled, demLayerKey])

  return { status }
}
